/**
 * In-memory API Cache and Request Deduplication Utility
 * Prevents redundant duplicate network requests, pools in-flight requests,
 * and caches response data with configurable TTL.
 */

const inFlightRequests = new Map();
const responseCache = new Map();

/**
 * Perform a cached and deduplicated fetch request.
 *
 * @param {string} url - The URL to fetch.
 * @param {RequestInit} [options={}] - Standard fetch options (method, headers, signal, etc.).
 * @param {number} [ttlMs=60000] - Cache Time-To-Live in milliseconds (default: 60s). Set to 0 to disable caching (deduplication only).
 * @returns {Promise<{ ok: boolean, status: number, data: any, fromCache?: boolean }>}
 */
export const cachedFetch = async (url, options = {}, ttlMs = 60000) => {
  const method = (options.method || 'GET').toUpperCase();
  const cacheKey = `${method}:${url}`;

  // Only GET requests are cached and deduplicated
  if (method !== 'GET') {
    const response = await fetch(url, options);
    const data = await response.json().catch(() => null);
    return { ok: response.ok, status: response.status, data };
  }

  // 1. Check if we have a valid, non-expired cache entry
  const now = Date.now();
  const cached = responseCache.get(cacheKey);
  if (cached && now - cached.timestamp < cached.ttl) {
    return { ok: true, status: 200, data: cached.data, fromCache: true };
  }

  // 2. Check if an identical request is already in-flight (deduplication)
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  // 3. Initiate new fetch and register promise in inFlightRequests
  const fetchPromise = (async () => {
    try {
      const response = await fetch(url, options);
      const data = await response.json().catch(() => null);

      if (response.ok && ttlMs > 0 && data !== null) {
        responseCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          ttl: ttlMs,
        });
      }

      return { ok: response.ok, status: response.status, data };
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, fetchPromise);
  return fetchPromise;
};

/**
 * Clear cached entries. If a pattern is provided, clears keys matching the pattern/substring.
 * @param {string|RegExp} [pattern] - Optional pattern to match cache keys.
 */
export const clearApiCache = (pattern) => {
  if (!pattern) {
    responseCache.clear();
    return;
  }
  for (const key of responseCache.keys()) {
    if (typeof pattern === 'string' && key.includes(pattern)) {
      responseCache.delete(key);
    } else if (pattern instanceof RegExp && pattern.test(key)) {
      responseCache.delete(key);
    }
  }
};
